import exifr from 'exifr';
import imageCompression from 'browser-image-compression';

export interface PhotoMetadata {
  latitude: number | null;
  longitude: number | null;
  timestamp: string | null;
  hasLocation: boolean;
  hasTimestamp: boolean;
  debugInfo?: {
    fileName: string;
    fileSize: number;
    gpsFieldsFound: string[];
    rawGPSData?: any;
    fullExifData?: any;
    validationDetails?: {
      latitudePresent: boolean;
      longitudePresent: boolean;
      latitudeIsNumber: boolean;
      longitudeIsNumber: boolean;
      latitudeInRange: boolean;
      longitudeInRange: boolean;
      latitudeValue?: any;
      longitudeValue?: any;
    };
  };
}

export async function extractPhotoMetadata(file: File): Promise<PhotoMetadata> {
  console.log(`[GPS Debug] Extracting metadata from: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);

  try {
    let latitude: number | null = null;
    let longitude: number | null = null;
    let timestamp: string | null = null;
    let hasTimestamp = false;
    const gpsFieldsFound: string[] = [];
    let rawGPSData: any = {};

    try {
      console.log('[GPS Debug] Attempting GPS extraction using exifr.gps()...');
      const gpsData = await exifr.gps(file);

      if (gpsData) {
        console.log('[GPS Debug] GPS data from exifr.gps():', JSON.stringify(gpsData, null, 2));
        latitude = gpsData.latitude ?? null;
        longitude = gpsData.longitude ?? null;
        rawGPSData = { ...gpsData };

        if (latitude !== null) gpsFieldsFound.push('latitude (converted)');
        if (longitude !== null) gpsFieldsFound.push('longitude (converted)');
      } else {
        console.log('[GPS Debug] exifr.gps() returned null');
      }
    } catch (gpsError) {
      console.warn('[GPS Debug] exifr.gps() failed:', gpsError);
    }

    console.log('[GPS Debug] Attempting full EXIF parse...');
    const exif = await exifr.parse(file, {
      gps: true,
      tiff: true,
      exif: true,
      interop: true,
      ifd0: true,
      ifd1: true,
      iptc: false,
      xmp: false,
      icc: false,
      jfif: false,
      mergeOutput: true,
      translateKeys: true,
      translateValues: true,
      reviveValues: true
    });

    if (exif) {
      console.log('[GPS Debug] Full EXIF data:', JSON.stringify(exif, null, 2));
      console.log('[GPS Debug] EXIF keys found:', Object.keys(exif));

      if (latitude === null && exif.latitude !== undefined) {
        latitude = exif.latitude;
        gpsFieldsFound.push('exif.latitude');
      }
      if (longitude === null && exif.longitude !== undefined) {
        longitude = exif.longitude;
        gpsFieldsFound.push('exif.longitude');
      }

      // Handle raw GPS coordinates if needed
      if (latitude === null && exif.GPSLatitude !== undefined) {
        console.log('[GPS Debug] Processing GPSLatitude:', exif.GPSLatitude, 'Type:', typeof exif.GPSLatitude);
        // Check if it's already a number or needs conversion
        if (typeof exif.GPSLatitude === 'number') {
          latitude = exif.GPSLatitude;
          console.log('[GPS Debug] GPSLatitude is a number:', latitude);
        } else if (Array.isArray(exif.GPSLatitude) && exif.GPSLatitude.length === 3) {
          // Convert from [degrees, minutes, seconds] format
          const [deg, min, sec] = exif.GPSLatitude;
          console.log('[GPS Debug] Converting DMS to decimal - Deg:', deg, 'Min:', min, 'Sec:', sec);
          latitude = deg + min / 60 + sec / 3600;
          console.log('[GPS Debug] Converted latitude (before ref):', latitude);
          // Apply hemisphere
          if (exif.GPSLatitudeRef === 'S') {
            latitude = -latitude;
            console.log('[GPS Debug] Applied South hemisphere, new latitude:', latitude);
          }
        } else {
          console.log('[GPS Debug] GPSLatitude format not recognized:', exif.GPSLatitude);
        }
        if (latitude !== null) {
          gpsFieldsFound.push('GPSLatitude');
        }
      }

      if (longitude === null && exif.GPSLongitude !== undefined) {
        console.log('[GPS Debug] Processing GPSLongitude:', exif.GPSLongitude, 'Type:', typeof exif.GPSLongitude);
        // Check if it's already a number or needs conversion
        if (typeof exif.GPSLongitude === 'number') {
          longitude = exif.GPSLongitude;
          console.log('[GPS Debug] GPSLongitude is a number:', longitude);
        } else if (Array.isArray(exif.GPSLongitude) && exif.GPSLongitude.length === 3) {
          // Convert from [degrees, minutes, seconds] format
          const [deg, min, sec] = exif.GPSLongitude;
          console.log('[GPS Debug] Converting DMS to decimal - Deg:', deg, 'Min:', min, 'Sec:', sec);
          longitude = deg + min / 60 + sec / 3600;
          console.log('[GPS Debug] Converted longitude (before ref):', longitude);
          // Apply hemisphere
          if (exif.GPSLongitudeRef === 'W') {
            longitude = -longitude;
            console.log('[GPS Debug] Applied West hemisphere, new longitude:', longitude);
          }
        } else {
          console.log('[GPS Debug] GPSLongitude format not recognized:', exif.GPSLongitude);
        }
        if (longitude !== null) {
          gpsFieldsFound.push('GPSLongitude');
        }
      }

      if (exif.GPSLatitudeRef) gpsFieldsFound.push('GPSLatitudeRef');
      if (exif.GPSLongitudeRef) gpsFieldsFound.push('GPSLongitudeRef');
      if (exif.GPSAltitude !== undefined) gpsFieldsFound.push('GPSAltitude');
      if (exif.GPSTimeStamp) gpsFieldsFound.push('GPSTimeStamp');
      if (exif.GPSDateStamp) gpsFieldsFound.push('GPSDateStamp');

      rawGPSData = {
        ...rawGPSData,
        GPSLatitude: exif.GPSLatitude,
        GPSLongitude: exif.GPSLongitude,
        GPSLatitudeRef: exif.GPSLatitudeRef,
        GPSLongitudeRef: exif.GPSLongitudeRef,
        GPSAltitude: exif.GPSAltitude,
        latitude: exif.latitude,
        longitude: exif.longitude
      };

      const dateTimeOriginal = exif.DateTimeOriginal || exif.CreateDate || exif.DateTime;
      if (dateTimeOriginal) {
        try {
          const date = dateTimeOriginal instanceof Date
            ? dateTimeOriginal
            : new Date(dateTimeOriginal);

          if (!isNaN(date.getTime())) {
            timestamp = date.toISOString();
            hasTimestamp = true;
            console.log('[GPS Debug] Timestamp extracted:', timestamp);
          }
        } catch (error) {
          console.error('[GPS Debug] Error parsing timestamp:', error);
        }
      } else {
        console.log('[GPS Debug] No timestamp fields found');
      }
    } else {
      console.log('[GPS Debug] Full EXIF parse returned null');
    }

    console.log(`[GPS Debug] GPS fields found: [${gpsFieldsFound.join(', ')}]`);
    console.log(`[GPS Debug] Final coordinates: lat=${latitude}, lon=${longitude}`);

    const validationDetails = {
      latitudePresent: latitude !== null,
      longitudePresent: longitude !== null,
      latitudeIsNumber: latitude !== null && !isNaN(latitude),
      longitudeIsNumber: longitude !== null && !isNaN(longitude),
      latitudeInRange: latitude !== null && latitude >= -90 && latitude <= 90,
      longitudeInRange: longitude !== null && longitude >= -180 && longitude <= 180,
      latitudeValue: latitude,
      longitudeValue: longitude
    };

    console.log('[GPS Debug] Validation breakdown:', validationDetails);

    const hasValidLocation =
      latitude !== null &&
      longitude !== null &&
      !isNaN(latitude) &&
      !isNaN(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180;

    console.log(`[GPS Debug] Location valid: ${hasValidLocation}`);

    return {
      latitude: hasValidLocation ? latitude : null,
      longitude: hasValidLocation ? longitude : null,
      timestamp,
      hasLocation: hasValidLocation,
      hasTimestamp,
      debugInfo: {
        fileName: file.name,
        fileSize: file.size,
        gpsFieldsFound,
        rawGPSData,
        fullExifData: exif,
        validationDetails
      }
    };
  } catch (error) {
    console.error('[GPS Debug] Error extracting EXIF data:', error);
    return {
      latitude: null,
      longitude: null,
      timestamp: null,
      hasLocation: false,
      hasTimestamp: false,
      debugInfo: {
        fileName: file.name,
        fileSize: file.size,
        gpsFieldsFound: [],
        rawGPSData: { error: error instanceof Error ? error.message : 'Unknown error' }
      }
    };
  }
}

export async function compressImage(file: File, maxSize: number = 1200): Promise<Blob> {
  console.log('[GPS Debug] Starting image compression with EXIF preservation...');
  console.log('[GPS Debug] Original file size:', (file.size / 1024).toFixed(2), 'KB');

  try {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: maxSize,
      useWebWorker: true,
      preserveExif: true,
      fileType: 'image/jpeg',
      initialQuality: 0.8
    };

    const compressedFile = await imageCompression(file, options);
    console.log('[GPS Debug] Compressed file size:', (compressedFile.size / 1024).toFixed(2), 'KB');

    const compressedExif = await exifr.gps(compressedFile);
    console.log('[GPS Debug] EXIF preserved in compressed image:', compressedExif);

    return compressedFile;
  } catch (error) {
    console.error('[GPS Debug] Error during compression with EXIF preservation:', error);
    throw error;
  }
}
